import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { CompanyDetail, CustomFieldValue } from "../../lib/crm-types";
import { cn } from "../ui/cn";

/**
 * The fields a manager added, and this company's answers to them.
 *
 * Edited in place, one field at a time, saving on blur. There is no form and no
 * save button: these are single values with no relationship to each other, and
 * wrapping eleven independent fields in one submit means a typo in one blocks
 * the other ten.
 */
export function CustomFieldsPanel({ company }: { company: CompanyDetail }) {
  if (company.customFields.length === 0) {
    return null;
  }

  return (
    <section className="rounded-lg border border-line">
      <header className="border-b border-line px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">
          More
        </h2>
      </header>
      <div className="divide-y divide-line">
        {company.customFields.map((field) => (
          <FieldRow key={field.id} companyId={company.id} field={field} />
        ))}
      </div>
    </section>
  );
}

function FieldRow({
  companyId,
  field,
}: {
  companyId: number;
  field: CustomFieldValue;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(field.value ?? "");

  const save = useMutation({
    mutationFn: (next: string) =>
      crmApi.setCustomField(companyId, field.id, next.trim() || null),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompany(companyId),
      });
    },
    onError: () => {
      // Put back what is actually stored. Leaving the rejected value on screen
      // would look saved.
      setValue(field.value ?? "");
    },
  });

  const commit = (next: string): void => {
    if (next.trim() === (field.value ?? "").trim()) return;
    save.mutate(next);
  };

  const shared = cn(
    "w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-xs text-content",
    "hover:border-line focus:border-brand-400 focus:outline-none",
    save.isPending && "opacity-50",
  );

  return (
    <div className="px-3 py-1.5">
      <div className="flex items-center gap-2">
        <label
          htmlFor={`field-${field.id}`}
          className="w-28 shrink-0 text-xs text-faint"
        >
          {field.label}
        </label>

        <div className="min-w-0 flex-1">
          {field.type === "dropdown" ? (
            <select
              id={`field-${field.id}`}
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
                commit(event.target.value);
              }}
              className={cn(shared, value ? "" : "text-faint")}
            >
              <option value="">Not set</option>
              {(field.options ?? []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <input
              id={`field-${field.id}`}
              type={
                field.type === "number"
                  ? "number"
                  : field.type === "date"
                    ? "date"
                    : "text"
              }
              value={value}
              placeholder="—"
              onChange={(event) => setValue(event.target.value)}
              onBlur={(event) => commit(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
                if (event.key === "Escape") {
                  setValue(field.value ?? "");
                  event.currentTarget.blur();
                }
              }}
              className={shared}
            />
          )}
        </div>
      </div>

      {field.helpText ? (
        <p className="ml-30 pl-2 text-[11px] text-faint">{field.helpText}</p>
      ) : null}

      {save.isError ? (
        <p className="ml-30 pl-2 text-[11px] text-sla-breached">
          {save.error instanceof ApiError
            ? save.error.message
            : "Could not save that."}
        </p>
      ) : null}
    </div>
  );
}
